<script>
export default {
    name: 'BaseSnapScroll',
};
</script>

<script setup>
import { debounce } from 'lodash-es';
import { ref, computed, onMounted, watch } from 'vue';
import { addUnit } from '@/components/base-components/utils/dom';
import { ORIENTATION, SNAP_POSITION } from '@/components/base-components/components/snap-scroll/index.js';

const props = defineProps({
    value:         {
        type: Number,
    },
    items:         {
        type:     Array,
        required: true,
    },
    orientation:   {
        type:      String,
        default:   ORIENTATION.VERTICAL,
        validator: value => Object.values(ORIENTATION).includes(value),
    },
    snapPosition:  {
        type:      String,
        default:   SNAP_POSITION.CENTER,
        validator: value => Object.values(SNAP_POSITION).includes(value),
    },
    loop:          {
        type:    Boolean,
        default: false,
    },
    repeat:        {
        type:      Number,
        default:   3,
        validator: value => value >= 3 && value % 2,
    },
    height:        {
        type:      Number,
        default:   40,
        validator: value => value > 0,
    },
    visibleCount:  {
        type:      Number,
        default:   5,
        validator: value => value >= 3 && value % 2,
    },
    viewClass:     {
        type: [String, Array, Object],
    },
    itemWrapClass: {
        type: [String, Array, Object],
    },
});

const emit = defineEmits({
    input: null,
});

const model = computed({
    get: () => props.value ?? 0,
    set: value => emit('input', value),
});

const isVertical = computed(() => props.orientation === 'vertical');
const sizeProperty = computed(() => isVertical.value ? 'height' : 'width');
const scrollProperty = computed(() => isVertical.value ? 'top' : 'left');
const containerProperty = computed(() => isVertical.value ? 'scrollTop' : 'scrollLeft');
const startPaddingProperty = computed(() => isVertical.value ? 'paddingTop' : 'paddingLeft');
const endPaddingProperty = computed(() => isVertical.value ? 'paddingBottom' : 'paddingRight');
const snapClass = computed(() => ({
    [SNAP_POSITION.START]:  'snap-start',
    [SNAP_POSITION.CENTER]: 'snap-center',
    [SNAP_POSITION.END]:    'snap-end',
}[props.snapPosition]));

const extendedItems = computed(() => props.loop ? Array(props.repeat).fill(props.items).flat() : props.items);

const container = ref(null);
const centerBlockIndex = computed(() => Math.floor(props.repeat / 2));
const topIndexShift = computed(() => ({
    [SNAP_POSITION.START]:  0,
    [SNAP_POSITION.CENTER]: Math.floor(props.visibleCount / 2),
    [SNAP_POSITION.END]:    props.visibleCount - 1,
}[props.snapPosition]));
const bottomIndexShift = computed(() => ({
    [SNAP_POSITION.START]:  props.visibleCount - 1,
    [SNAP_POSITION.CENTER]: Math.floor(props.visibleCount / 2),
    [SNAP_POSITION.END]:    0,
}[props.snapPosition]));

const isScrolling = ref(false);

function scrollTo(index, behavior = 'instant') {
    if (!container.value) return;

    container.value.classList.remove('snap-both');

    requestAnimationFrame(() => {
        const centerIndex = props.loop
            ? centerBlockIndex.value * props.items.length + index - topIndexShift.value
            : index;

        container.value.scrollTo({
            [scrollProperty.value]: centerIndex * props.height,
            behavior,
        });

        requestAnimationFrame(() => {
            container.value.classList.add('snap-both');
        });
    });
}

onMounted(() => {
    scrollTo(model.value);
});

watch(() => props.value, (value, oldValue) => {
    if (value === oldValue || isScrolling.value) return;

    scrollTo(value, 'smooth');
});

const onAfterScroll = debounce(() => {
    if (!container.value) return;

    const index = Math.round(container.value[containerProperty.value] / props.height);
    const total = props.items.length;
    const relativeIndex = props.loop ? (index % total) + topIndexShift.value : index;

    model.value = relativeIndex % total;
    isScrolling.value = false;

    if (props.loop && (index < total || index > total * (props.repeat - 1))) {
        scrollTo(relativeIndex);
    }
}, 100);

function onScrolling() {
    isScrolling.value = true;

    onAfterScroll();
}

function activate(index) {
    model.value = index % props.items.length;

    scrollTo(model.value);
}

defineExpose({
    scrollTo,
});
</script>

<template>
    <div class="relative">
        <div
            ref="container"
            class="snap-mandatory overflow-scroll scrollbar-hidden"
            :class="viewClass"
            :style="{
                [sizeProperty]: addUnit(height * visibleCount),
                [startPaddingProperty]: !loop ? addUnit(height * topIndexShift) : undefined,
                [endPaddingProperty]: !loop ? addUnit(height * bottomIndexShift) : undefined,
            }"
            @scroll.passive="onScrolling"
        >
            <div
                v-for="(item, index) in extendedItems"
                :key="index"
                class="shrink-0"
                :class="[
                    snapClass,
                    itemWrapClass,
                ]"
                :style="{ [sizeProperty]: addUnit(height) }"
            >
                <slot
                    name="item"
                    :item="item"
                    :index="index % items.length"
                    :active="(index % items.length) === model"
                    :activate="() => activate(index)"
                    :is-scrolling="isScrolling"
                >
                    {{ item }}
                </slot>
            </div>
        </div>

        <slot />
    </div>
</template>
